import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Search, LayoutDashboard, LogOut, User, Settings, Users, UserCircle,
  BarChart3, FolderOpen, Sliders, TrendingUp, MessageCircle, Mail,
  AlertCircle, FileText, Send, ClipboardList, Crosshair, ChevronsLeft, ChevronsRight,
  Shield, GraduationCap, Lock, Building2, Briefcase, Sparkles, Landmark, Activity, Clock, Pill,
  KeyRound, PieChart, ChevronDown, ShieldCheck, Award, Plug,
} from 'lucide-react';

// Ícone de sistema plugado (ADR 0016): o manifesto diz o nome; fora desta lista, Plug.
const ICONES_PLUGADOS = { ShieldCheck, Pill, FileText, ClipboardList, Activity, Building2, Briefcase, Landmark, Shield, Award, Plug };
import { useBranding, logoHeader } from '../context/BrandingContext';
import FloatingChat from './FloatingChat';
import NotificacoesBell from './NotificacoesBell';
import AlterarSenhaModal from './AlterarSenhaModal';
import { authAPI } from '../services/api';

// Subitens do módulo Inovação — aparecem indentados quando o usuário está na área
// (resolve "19 rotas atrás de 1 link"). O link pai "Inovação (GNI)" é o Painel.
const INOV_SUBITENS = [
  { to: '/inovacao/analises', label: 'Análises' },
  { to: '/inovacao/projetos', label: 'Projetos' },
  { to: '/inovacao/meu-trabalho', label: 'Meu trabalho' },
  { to: '/inovacao/carga-pessoas', label: 'Carga de pessoas' },
  { to: '/inovacao/gantt', label: 'Gantt consolidado' },
  { to: '/inovacao/dashboard', label: 'Dashboard' },
  { to: '/inovacao/templates', label: 'Templates' },
];

// Subitens da área Contabilidade (mesmo padrão do Inovação). O pai abre a área;
// os subitens aparecem quando o usuário está numa das telas dela.
const CONT_SUBITENS = [
  { to: '/resultado-plano', label: 'Resultado por Plano' },
  { to: '/contabilidade/demonstracoes', label: 'Demonstrações Contábeis' },
];
const naContabilidade = (path) => path.startsWith('/contabilidade') || path.startsWith('/resultado-plano');

// Label separadora de seção
const SectionLabel = ({ label, hidden }) => hidden ? null : (
  <li style={{ padding: '1.1rem 0.75rem 0.3rem', pointerEvents: 'none' }}>
    <span style={{
      fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
      color: 'var(--gray-400)', textTransform: 'uppercase',
    }}>
      {label}
    </span>
  </li>
);

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { branding, pronto } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Submenu de Gestão de Projetos: clicar de novo no item pai recolhe/expande
  const [inovAberto, setInovAberto] = useState(true);
  const [contAberto, setContAberto] = useState(true);
  const mainRef = React.useRef(null);

  // Ao trocar de página, volta a rolagem pro topo — o container .main-content
  // preserva a posição anterior e a página nova parecia abrir "no final".
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Lista de códigos de módulos liberados ao usuário (vem do backend resolvendo
  // perfil + overrides individuais). Enquanto carrega, fica null.
  const [modulosCodigos, setModulosCodigos] = useState(null);
  // Map codigo -> bloqueado (cadeado ativo para não-admin). Vem do backend.
  const [modulosBloqueados, setModulosBloqueados] = useState(new Set());
  // Sistemas plugados (ADR 0016): entram no menu pelo catálogo, sem código por sistema.
  const [modulosPlugados, setModulosPlugados] = useState([]);
  // Modal do cadeado: { codigo, nome } quando aberto
  const [cadeadoModal, setCadeadoModal] = useState(null);

  useEffect(() => {
    if (!user) return;
    authAPI.getModulos()
      .then(({ data }) => {
        const mods = data?.modulos || [];
        setModulosCodigos(new Set(mods.map(m => m.codigo)));
        setModulosBloqueados(new Set(mods.filter(m => m.bloqueado).map(m => m.codigo)));
        setModulosPlugados(mods.filter(m => (m.rota || '').startsWith('/plug/')));
      })
      .catch(() => setModulosCodigos(new Set())); // erro = nada permitido
  }, [user]);

  // Helper: verifica se o usuário pode acessar um módulo pelo código
  const pode = (codigo) => modulosCodigos === null || modulosCodigos.has(codigo);
  // Helper: módulo aparece mas está com cadeado (só não-admin, interruptor ligado)
  const estaBloqueado = (codigo) => modulosBloqueados.has(codigo);

  // Verifica se ao menos um módulo de uma seção está liberado (controla SectionLabel)
  const temAlgumDaSecao = (codigos) => codigos.some(c => pode(c));

  // Item de menu: renderiza NavLink normal ou, se bloqueado, um cadeado que abre o modal
  const Item = ({ codigo, to, icon, label, end, onClick }) => {
    if (!pode(codigo)) return null;
    if (estaBloqueado(codigo)) {
      return (
        <li>
          <a
            title={`${label} (bloqueado)`}
            onClick={() => setCadeadoModal({ codigo, nome: label })}
            style={{ cursor: 'pointer', opacity: 0.55, display: 'flex', alignItems: 'center' }}
          >
            {icon}
            {!sidebarCollapsed && <span style={{ flex: 1 }}>{label}</span>}
            {!sidebarCollapsed && <Lock size={13} style={{ marginLeft: 'auto', flexShrink: 0 }} />}
          </a>
        </li>
      );
    }
    return (
      <li>
        <NavLink to={to} end={end} onClick={onClick} className={({ isActive }) => isActive ? 'active' : ''} title={label}>
          {icon}
          {!sidebarCollapsed && label}
        </NavLink>
      </li>
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const [showSenha, setShowSenha] = useState(false);
  // Menu do usuário (nome/ícone no cabeçalho): Alterar minha senha, Sair.
  const [menuUser, setMenuUser] = useState(false);
  const menuRef = React.useRef(null);
  useEffect(() => {
    if (!menuUser) return undefined;
    const fora = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuUser(false); };
    const esc = (e) => { if (e.key === 'Escape') setMenuUser(false); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc); };
  }, [menuUser]);
  const itemMenu = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '0.6rem 0.9rem',
    background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--gray-700, #333)',
  };

  return (
    <div className="app-container">
      {/* Header */}
      <header className="header">
        {pronto
          // logo em branco sobre a faixa da marca (pedido do Victor, QA 2026-09): o logo
          // da operadora é colorido e sumia no cabeçalho (verde sobre verde). O filtro
          // vira silhueta branca — exige PNG com fundo transparente (o upload atual é).
          ? <img src={logoHeader(branding)} alt={branding.nome} style={{ height: '40px', filter: 'brightness(0) invert(1)' }} />
          : <div aria-hidden="true" style={{ height: '40px', width: '120px' }} />}
        <div className="header-user">
          <NotificacoesBell />
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className="btn-logout"
              onClick={() => setMenuUser((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuUser}
              aria-label="Menu do usuário"
              title="Minha conta"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <User size={18} />
              <span>{user?.nome_completo || 'Usuario'}</span>
              <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>({user?.perfil?.replace('_', ' ')})</span>
              <ChevronDown size={14} style={{ transition: 'transform 0.15s', transform: menuUser ? 'rotate(180deg)' : 'none' }} />
            </button>
            {menuUser && (
              <div role="menu" style={{
                position: 'absolute', right: 0, top: 'calc(100% + 6px)', minWidth: 220, zIndex: 100,
                background: '#fff', border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,.14)', padding: '0.35rem 0', overflow: 'hidden',
              }}>
                <div style={{ padding: '0.5rem 0.9rem 0.4rem', fontSize: '0.75rem', color: 'var(--gray-500, #777)', borderBottom: '1px solid var(--gray-100, #f0f0f0)' }}>
                  {user?.email}
                </div>
                <button type="button" role="menuitem" style={itemMenu} onClick={() => { setMenuUser(false); setShowSenha(true); }}>
                  <KeyRound size={16} /> Alterar minha senha
                </button>
                <button type="button" role="menuitem" style={{ ...itemMenu, color: '#c0392b' }} onClick={handleLogout}>
                  <LogOut size={16} /> Sair
                </button>
              </div>
            )}
          </div>
          <AlterarSenhaModal aberto={showSenha} onClose={() => setShowSenha(false)} />
        </div>
      </header>

      <div className="main-layout">
        {/* Sidebar */}
        <aside className="sidebar" style={{ width: sidebarCollapsed ? 56 : 250, transition: 'width 0.22s ease', overflowX: 'hidden', overflowY: 'auto', position: 'relative' }}>
          {/* Botão recolher */}
          <button
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            style={{
              position: 'absolute', top: 8, right: 8,
              background: 'none', border: '1px solid var(--gray-200)',
              borderRadius: 6, padding: '3px 5px', cursor: 'pointer',
              color: 'var(--gray-400)', display: 'flex', alignItems: 'center',
              zIndex: 10, transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            {sidebarCollapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
          </button>
          <nav style={{ marginTop: sidebarCollapsed ? 36 : 0 }}>
            <ul className="sidebar-menu" style={{ whiteSpace: 'nowrap' }}>

              {/* ── OPERACIONAL ── */}
              {temAlgumDaSecao(['kanban','busca','whatsapp','inovacao']) && (
                <SectionLabel label="Operacional" hidden={sidebarCollapsed} />
              )}
              <Item codigo="kanban" to="/kanban" label="Kanban / Chamados" icon={<LayoutDashboard size={18} />} />
              <Item codigo="busca" to="/busca" label="Buscar Beneficiário" icon={<Search size={18} />} />
              <Item codigo="whatsapp" to="/whatsapp" label="WhatsApp" icon={<MessageCircle size={18} />} />
              <Item
                codigo="inovacao" to="/inovacao" end label="Gestão de Projetos"
                icon={<TrendingUp size={18} />}
                onClick={() => {
                  // Já dentro do módulo: segundo clique recolhe o submenu.
                  if (location.pathname.startsWith('/inovacao')) setInovAberto(a => !a);
                  else setInovAberto(true);
                }}
              />
              {pode('inovacao') && !estaBloqueado('inovacao') && !sidebarCollapsed && inovAberto && location.pathname.startsWith('/inovacao') &&
                INOV_SUBITENS.map((s) => (
                  <li key={s.to}>
                    <NavLink to={s.to} title={s.label}
                             className={({ isActive }) => isActive ? 'active' : ''}
                             style={{ paddingLeft: 42, fontSize: '0.83rem' }}>
                      {s.label}
                    </NavLink>
                  </li>
                ))}

              {/* ── ANÁLISES ── */}
              {temAlgumDaSecao(['dashboard_atendimentos','dashboard_inadimplencia','pesquisas','radar_carteira','dfc','resultado_plano','indicadores_ans','relatorios']) && (
                <SectionLabel label="Análises" hidden={sidebarCollapsed} />
              )}
              <Item codigo="dashboard_atendimentos" to="/dashboard/atendimentos" label="Atendimentos" icon={<BarChart3 size={18} />} />
              <Item codigo="dashboard_inadimplencia" to="/dashboard/inadimplencia" label="Inadimplência" icon={<AlertCircle size={18} />} />
              <Item codigo="pesquisas" to="/admin/pesquisas" label="Pesquisas / NPS" icon={<TrendingUp size={18} />} />
              <Item codigo="radar_carteira" to="/radar-carteira" label="Radar da Carteira" icon={<Crosshair size={18} />} />
              <Item codigo="radar_carteira" to="/panorama-carteira" label="Panorama da Carteira" icon={<Users size={18} />} />
              <Item codigo="dfc" to="/dfc" label="DFC · Financeiro" icon={<BarChart3 size={18} />} />
              <Item
                codigo="resultado_plano" to="/contabilidade" end label="Contabilidade"
                icon={<PieChart size={18} />}
                onClick={(e) => {
                  // Já dentro da área: o clique só recolhe/abre o submenu — não troca
                  // de tela (antes navegava e desmontava a tela, perdendo abas e filtros).
                  if (naContabilidade(location.pathname)) { e.preventDefault(); setContAberto(a => !a); }
                  else setContAberto(true);
                }}
              />
              {pode('resultado_plano') && !estaBloqueado('resultado_plano') && !sidebarCollapsed && contAberto && naContabilidade(location.pathname) &&
                CONT_SUBITENS.map((s) => (
                  <li key={s.to}>
                    <NavLink to={s.to} title={s.label}
                             className={({ isActive }) => isActive ? 'active' : ''}
                             style={{ paddingLeft: 42, fontSize: '0.83rem' }}>
                      {s.label}
                    </NavLink>
                  </li>
                ))}
              <Item codigo="indicadores_ans" to="/indicadores-ans" label="Indicadores ANS" icon={<Award size={18} />} />
              <Item codigo="relatorios" to="/relatorios" label="Inteligência" icon={<Sparkles size={18} />} />

              {/* ── COMUNICAÇÕES ── */}
              {temAlgumDaSecao(['templates_email','emails','acompanhamento']) && (
                <SectionLabel label="Comunicações" hidden={sidebarCollapsed} />
              )}
              <Item codigo="templates_email" to="/admin/templates-email" label="Templates de Email" icon={<FileText size={18} />} />
              <Item codigo="emails" to="/admin/emails" label="Histórico de Envios" icon={<Send size={18} />} />
              <Item codigo="acompanhamento" to="/admin/acompanhamento" label="Acompanhamento" icon={<ClipboardList size={18} />} />

              {/* ── ADMINISTRAÇÃO ── */}
              {temAlgumDaSecao(['admin_operadora','admin_usuarios','admin_setores','admin_beneficiarios','admin_qualidade_dado','admin_documentos','admin_config_atendimentos','auditoria_modulos','auditoria_bancaria']) && (
                <SectionLabel label="Administração" hidden={sidebarCollapsed} />
              )}
              <Item codigo="admin_operadora" to="/admin/operadora" label="Operadora" icon={<Building2 size={18} />} />
              <Item codigo="admin_usuarios" to="/admin/usuarios" label="Usuários" icon={<Users size={18} />} />
              <Item codigo="admin_usuarios" to="/admin/perfis-acesso" label="Perfis de acesso" icon={<KeyRound size={18} />} />
              <Item codigo="admin_usuarios" to="/admin/historico-acessos" label="Histórico de Acessos" icon={<Clock size={18} />} />
              <Item codigo="admin_usuarios" to="/admin/sistemas-plugados" label="Sistemas plugados" icon={<Plug size={18} />} />
              <Item codigo="admin_setores" to="/admin/setores" label="Setores" icon={<Settings size={18} />} />
              <Item codigo="admin_beneficiarios" to="/admin/beneficiarios" label="Beneficiários" icon={<UserCircle size={18} />} />
              <Item codigo="admin_qualidade_dado" to="/admin/qualidade-dado" label="Qualidade do dado" icon={<ShieldCheck size={18} />} />
              <Item codigo="admin_documentos" to="/admin/documentos" label="Documentos" icon={<FolderOpen size={18} />} />
              <Item codigo="admin_config_atendimentos" to="/admin/config-atendimentos" label="Config. Atendimentos" icon={<Sliders size={18} />} />
              <Item codigo="admin_config_atendimentos" to="/admin/cardapio-tarefas" label="Cardápio de tarefas" icon={<ClipboardList size={18} />} />
              <Item codigo="auditoria_modulos" to="/admin/auditoria-modulos" label="Auditoria de Módulos" icon={<Shield size={18} />} />
              <Item codigo="auditoria_bancaria" to="/auditoria-bancaria" label="Auditoria Bancária" icon={<Landmark size={18} />} />

              {/* ── SERVIÇOS (módulos Sabor B embutidos por iframe — ADR 0012) ── */}
              {temAlgumDaSecao(['brasindice', ...modulosPlugados.map(m => m.codigo)]) && (
                <SectionLabel label="Serviços" hidden={sidebarCollapsed} />
              )}
              <Item codigo="brasindice" to="/servicos/brasindice" label="Importação Brasíndice" icon={<Pill size={18} />} />
              {modulosPlugados.map(m => {
                const Icone = ICONES_PLUGADOS[m.icone] || Plug;
                return <Item key={m.codigo} codigo={m.codigo} to={m.rota} label={m.nome} icon={<Icone size={18} />} />;
              })}

              {/* ── UNIVERSIDADE ── */}
              {temAlgumDaSecao(['academia']) && (
                <SectionLabel label="Universidade" hidden={sidebarCollapsed} />
              )}
              <Item codigo="academia" to="/academia" label="Universidade" icon={<GraduationCap size={18} />} />

              {/* ── FABRICANTE (só instância do dono do produto, via override) ── */}
              {temAlgumDaSecao(['fabricante']) && (
                <SectionLabel label="Fabricante" hidden={sidebarCollapsed} />
              )}
              <Item codigo="fabricante" to="/fabricante/empresas" label="Gestão de Empresas" icon={<Briefcase size={18} />} />
              <Item codigo="fabricante" to="/fabricante/trilha" label="Trilha de Acesso" icon={<Activity size={18} />} />

            </ul>
          </nav>
        </aside>

        <main className="main-content" ref={mainRef}>
          <Outlet />
        </main>
      </div>

      {cadeadoModal && (
        <CadeadoModal
          modulo={cadeadoModal}
          onClose={() => setCadeadoModal(null)}
        />
      )}

      <FloatingChat />
    </div>
  );
};

// Modal do cadeado: explica que o módulo não está liberado e permite solicitar acesso.
const CadeadoModal = ({ modulo, onClose }) => {
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // 'ok' | 'erro'

  const solicitar = async () => {
    setEnviando(true);
    try {
      const { data } = await authAPI.solicitarLiberacaoModulo(modulo.codigo);
      setResultado(data?.enviado ? 'ok' : 'erro');
    } catch {
      setResultado('erro');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: '1.75rem',
          width: 'min(440px, 92vw)', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: '#f0f9f7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand, #20a490)',
          }}>
            <Lock size={20} />
          </div>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{modulo.nome}</h3>
        </div>

        {resultado === 'ok' ? (
          <p style={{ color: 'var(--gray-600)', lineHeight: 1.5 }}>
            Solicitação enviada. O administrador vai avaliar a liberação deste módulo.
          </p>
        ) : (
          <>
            <p style={{ color: 'var(--gray-600)', lineHeight: 1.5, marginTop: 0 }}>
              Este módulo ainda não está liberado para o seu acesso. Você pode solicitar a
              liberação ao administrador.
            </p>
            {resultado === 'erro' && (
              <p style={{ color: '#e74c3c', fontSize: '0.88rem' }}>
                Não foi possível enviar a solicitação agora. Tente novamente mais tarde.
              </p>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid var(--gray-200)',
              background: '#fff', cursor: 'pointer',
            }}
          >
            {resultado === 'ok' ? 'Fechar' : 'Cancelar'}
          </button>
          {resultado !== 'ok' && (
            <button
              onClick={solicitar}
              disabled={enviando}
              style={{
                padding: '0.55rem 1rem', borderRadius: 8, border: 'none',
                background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', cursor: enviando ? 'default' : 'pointer',
                opacity: enviando ? 0.7 : 1,
              }}
            >
              {enviando ? 'Enviando…' : 'Solicitar liberação'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
